import {
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList
} from "@solomon-labs/design/components/ui";
import type { ReactElement } from "react";
import { useMemo } from "react";

import { SELECTABLE_COUNTRIES } from "@/data/countries";

/**
 * Searchable country picker for the onboarding "Jurisdiction of Incorporation"
 * field (lite + full flows). Lists every ISO 3166-1 country except the
 * sanctioned set (see `SELECTABLE_COUNTRIES`). Selecting a country reports its
 * uppercase alpha-2 `code` — what we persist and send to yaas-api — and clearing
 * reports `null` so callers' required-field checks treat it as no selection.
 * Incoming `value` is matched case-insensitively so legacy lowercase rows (e.g.
 * `"us"`) still resolve to their display country.
 *
 * Items are `{ value, label }` objects: Base UI's Combobox auto-uses `label`
 * for display + filtering and `value` for the committed value, and
 * `ComboboxCollection`'s render-prop is what actually applies the text filter
 * (mapping `items` manually in JSX would render every country regardless of the
 * query — see the note in `shared/.../ui/combobox.tsx`).
 */

interface CountryItem {
    value: string;
    label: string;
}

const ITEMS: readonly CountryItem[] = SELECTABLE_COUNTRIES.map((c) => ({
    value: c.code,
    label: c.name
}));

export function CountrySelect({
    value,
    onChange,
    disabled = false,
    error = false,
    id
}: {
    value: string | null;
    onChange: (code: string | null) => void;
    disabled?: boolean;
    /** Renders the error border (the field's `FieldError` message is owned by
     *  the caller). */
    error?: boolean;
    id?: string;
}): ReactElement {
    const selected = useMemo(
        () => ITEMS.find((item) => item.value === value?.toUpperCase()) ?? null,
        [value]
    );

    return (
        <Combobox
            items={ITEMS}
            value={selected}
            onValueChange={(item: CountryItem | null) =>
                onChange(item ? item.value : null)
            }
            disabled={disabled}
        >
            <ComboboxInput
                id={id}
                placeholder="Search countries"
                disabled={disabled}
                /* `state`, not a raw `aria-invalid`: the error rail is drawn by
                   COMBOBOX_STATE_RAIL on the wrapping InputGroup, and
                   InputGroupInput sets `aria-invalid:border-0` so a nested
                   input cannot draw its own. The bare attribute named the
                   error for screen readers and showed nothing — this field
                   stayed neutral while its four siblings turned red.
                   `state="error"` sets aria-invalid itself. */
                state={error ? "error" : undefined}
            />
            <ComboboxContent>
                <ComboboxEmpty>No matching country.</ComboboxEmpty>
                <ComboboxList>
                    <ComboboxCollection>
                        {(item: CountryItem) => (
                            <ComboboxItem key={item.value} value={item}>
                                {item.label}
                            </ComboboxItem>
                        )}
                    </ComboboxCollection>
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}
